<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      :active-step="pageTitle"
    />
    <PageHeading
      :default-heading="pageTitle"
      :edit-heading="pageTitle"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          Add any other trustees and grantors
        </h2>
        <p>
          Your account needs at minimum one trustee and one grantor, but you can have up to 4 trustees
          and 6 grantors.
          <a
            id="trust-role-modal-trigger"
            href="javascript:void(0);"
          > What is a trustee or a grantor?</a>
          <pvd-modal
            id="trust-role-modal"
            for="trust-role-modal-trigger"
            class="small"
          >
            <div slot="modal-header">
              <h2 class="pvd-heading-L">
                Trust roles
              </h2>
            </div>
            <div slot="modal-body">
              <div class="pvd-container">
                <p>
                  <strong>Trustee</strong><br>
                  The trustee is a person or institution who has control of a trust’s assets and has a fiduciary
                  responsibility to appropriately handle the assets. A Fidelity trust account needs at least one
                  trustee, with a maximum number of 4.
                </p>
                <p>
                  <strong>Grantor</strong><br>
                  A grantor is the person who creates or contributes the assets to a trust. A Fidelity trust account
                  needs at least one grantor, with a maximum of 6.
                </p>
              </div>
            </div>
          </pvd-modal>
        </p>
        <div class="table-container">
          <table
            class="pvd-table-simple"
          >
            <thead>
              <tr>
                <th class="text-left-1pl">
                  Name
                </th>
                <th class="text-left-1pl">
                  Role
                </th>
                <th class="text-left-1pl">
                  Authorities
                </th>
                <th class="text-left-1pl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="applicant.additionalDetails">
                <td class="text-left-1pl">
                  {{ applicant.firstName }} {{ applicant.middleName }} {{ applicant.lastName }}
                </td>
                <td class="text-left-1pl">
                  {{ formatRole(applicant.additionalDetails.role) }}
                </td>
                <td class="text-left-1pl">
                  <span
                    v-show="applicant.additionalDetails.hasAmendRevoke"
                  >
                    {{ applicant.additionalDetails.hasAmend ? 'Amend' : '' }}{{ applicant.additionalDetails.hasAmend &&
                      applicant.additionalDetails.hasRevoke ? '/' : '' }}{{ applicant.additionalDetails.hasRevoke ?
                      applicant.additionalDetails.hasAmend ?
                        'revoke' : 'Revoke' : '' }} trust{{ applicant.additionalDetails.hasAmendRevoke &&
                      applicant.additionalDetails.hasAppointRemove ? ';'
                      : '' }}
                  </span>
                  <span
                    v-show="applicant.additionalDetails.hasAppointRemove"
                  >
                    Appoint/remove trustees
                  </span>
                </td>
                <td class="text-left-1pl actions-column">
                  <a
                    href="javascript:void(0);"
                    @click.prevent="editPrimaryUser()"
                  >
                    Edit
                  </a>
                </td>
              </tr>
              <tr
                v-for="(person, index) in additionalRoles"
                :key="index"
              >
                <div
                  v-if="person.role == 'trustee' || person.role == 'grantor' || person.role == 'trustee/grantor'"
                  class="display-contents"
                >
                  <td class="text-left-1pl">
                    {{ person.firstName }} {{ person.middleName }} {{ person.lastName }}
                  </td>
                  <td class="text-left-1pl">
                    {{ formatRole(person.role) }}
                  </td>
                  <td class="text-left-1pl">
                    <span
                      v-show="person.hasAmendRevoke"
                    >
                      {{ person.hasAmend ? 'Amend' : '' }}{{ person.hasAmend &&
                        person.hasRevoke ? '/' : '' }}{{ person.hasRevoke ?
                        person.hasAmend ?
                          'revoke' : 'Revoke' : '' }} trust{{ person.hasAmendRevoke &&
                        person.hasAppointRemove ? ';'
                        : '' }}
                    </span>
                    <span
                      v-show="person.hasAppointRemove"
                    >
                      Appoint/remove trustees
                    </span>
                  </td>
                  <td class="text-left-1pl actions-column">
                    <a
                      href="javascript:void(0);"
                      class="pr-1"
                      @click.prevent="editUser(index, 'trust-additional-role')"
                    >
                      Edit
                    </a>
                    <a
                      id="delete-user-confirmation-modal-trigger"
                      href="javascript:void(0);"
                      @click="openConfirmationModal(index)"
                    >
                      Remove
                    </a>
                  </td>
                </div>
              </tr>
            </tbody>
          </table>

          <InlineMessage
            v-show="!grantorValid"
            message="You must add at least one grantor to continue."
            css-class="form-row-message p-medium-top p-large-bottom"
          />

          <InlineMessage
            v-show="maxRolesAdded"
            message="You've entered the maximum number of trustees and grantors for this account."
            css-class="form-row-message p-medium-top p-large-bottom"
            type="info"
          />

          <button
            v-if="!maxRolesAdded"
            class="pvd-btn btn-ghost-action add-margin"
            type="button"
            @click="addAnotherRole"
          >
            Add a trustee or grantor
          </button>
        </div>
      </div>

      <div class="pvd-container">
        <h2 class="pvd-heading-L">
          Add anyone else with authorities
        </h2>
        <p>
          If there’s anyone else besides the grantors and trustees who can amend the trust, revoke the trust,
          or appoint and remove trustees, add them here.
          <a
            id="trust-authorities-modal-trigger"
            href="javascript:void(0);"
          >What are authorities?</a>
          <pvd-modal
            id="trust-authorities-modal"
            for="trust-authorities-modal-trigger"
            class="small"
          >
            <div slot="modal-header">
              <h2 class="pvd-heading-L">
                Trust authorities
              </h2>
            </div>
            <div slot="modal-body">
              <div class="pvd-container">
                <p>
                  Trust authorities include the power to amend conditions of the trust,
                  revoke conditions of the trust, and the power to appoint or remove
                  individual trustees. The people who may hold these authorities can
                  vary by type of trust and the specific trust terms.
                  <br>
                  <br>
                  <strong>Refer to your trust documents</strong>
                  for this information when answering this question.
                </p>
              </div>
            </div>
          </pvd-modal>
        </p>
        <div class="table-container">
          <table
            v-if="hasAdditionalAuthorities"
            class="pvd-table-simple"
          >
            <thead>
              <tr>
                <th class="text-left-1pl">
                  Name
                </th>
                <th class="text-left-1pl">
                  Authorities
                </th>
                <th class="text-left-1pl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(person, index) in additionalRoles"
                :key="index"
              >
                <div
                  v-if="person.role == 'authority'"
                  class="display-contents"
                >
                  <td class="text-left-1pl">
                    {{ person.firstName }} {{ person.middleName }} {{ person.lastName }}
                  </td>
                  <td class="text-left-1pl">
                    <span
                      v-show="person.hasAmendRevoke"
                    >
                      {{ person.hasAmend ? 'Amend' : '' }}{{ person.hasAmend &&
                        person.hasRevoke ? '/' : '' }}{{ person.hasRevoke ?
                        person.hasAmend ?
                          'revoke' : 'Revoke' : '' }} trust{{ person.hasAmendRevoke &&
                        person.hasAppointRemove ? ';'
                        : '' }}
                    </span>
                    <span
                      v-show="person.hasAppointRemove"
                    >
                      Appoint/remove trustees
                    </span>
                  </td>
                  <td class="text-left-1pl actions-column">
                    <a
                      href="javascript:void(0);"
                      class="pr-1"
                      @click.prevent="editUser(index, 'trust-authority')"
                    >
                      Edit
                    </a>
                    <a
                      id="delete-user-confirmation-modal-trigger"
                      href="javascript:void(0);"
                      @click="openConfirmationModal(index)"
                    >
                      Remove
                    </a>
                  </td>
                </div>
              </tr>
            </tbody>
          </table>

          <InlineMessage
            v-show="maxAuthoritiesAdded"
            message="You've entered the maximum number of people with authority powers."
            css-class="form-row-message p-medium-top p-large-bottom"
            type="info"
          />

          <button
            v-if="!maxAuthoritiesAdded"
            class="pvd-btn btn-ghost-action add-margin"
            type="button"
            @click="addAnotherAuthority"
          >
            Add an individual
          </button>
        </div>
      </div>

      <pvd-modal
        v-if="additionalRoles.length > 0"
        id="delete-user-confirmation-modal"
        for="delete-user-confirmation-modal-trigger"
        class="small"
        :active="isConfirmationModalOpen"
      >
        <div slot="modal-header">
          <h2 class="pvd-heading-M">
            Are you sure you want to remove this person?
          </h2>
        </div>

        <div slot="modal-footer">
          <div class="pvd-btn-row">
            <div class="button-group">
              <button
                class="pvd-btn btn-action"
                @click.prevent="removeUser(activeIndex)"
              >
                Remove
              </button>
              <button
                class="pvd-btn btn-secondary"
                @click.prevent="closeConfirmationModal()"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </pvd-modal>
      <PageNavigation
        primary-button-type="button"
        :show-primary-button-spinner="shouldShowLoadingSpinner"
        :disable-primary-button="shouldDisableNext"
        :primary-button-label="primaryButtonLabel"
        :secondary-button-label="pd_content.secondary_button.default"
        :page-exit-url="pd_content.overlay_exit_link.overview_url"
        @primary-button-clicked="nextButtonClicked"
      />
    </form>
    <div
      id="fs-steps"
      class="personal-info-page"
      data-operation="IdentifyUser"
      data-step="Init"
      style="display: none"
    >
      &nbsp;
    </div>
  </div>
</template>

<script>
import ProgressBar from '../components/ProgressBar.vue';
import PageNavigation from '../components/PageNavigation.vue';
import PageHeading from '../components/PageHeading.vue';
import dataHelper from '../mixins/dataHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import widgetHelper from '../mixins/widgetHelper';
import InlineMessage from '../components/InlineMessage.vue';
import pageNavigationHelper from '../mixins/pageNavigationHelper';

export default {
  name: 'TrustDashboard',
  components: {
    ProgressBar,
    PageNavigation,
    PageHeading,
    InlineMessage,
  },
  mixins: [
    dataHelper,
    dataSanitize,
    measurement,
    utilityFunctions,
    validationHelper,
    widgetHelper,
    pageNavigationHelper,
  ],
  data() {
    return {
      backButtonAttempt: false,
      applicant: {},
      additionalRoles: [],
      accountDetails: {},
      hasAdditionalRoles: false,
      hasAdditionalAuthorities: false,
      grantorValid: true,
      MAX_TRUSTEES: 4,
      MAX_GRANTORS: 6,
      MAX_APPOINT_REMOVE: 6,
      MAX_AMEND_REVOKE: 6,
      activeIndex: null,
      isConfirmationModalOpen: false,
      isLoading: false,
      pageTitle: 'Who else is named in the trust?',
    };
  },
  computed: {
    shouldDisableNext() {
      return this.isLoading ? 'Y' : 'N';
    },
    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    primaryButtonLabel() {
      return this.isLoading ? this.pd_content.primary_button.wait : this.pd_content.primary_button.default;
    },
    maxRolesAdded: {
      get() {
        if (this.applicant && this.applicant.additionalDetails) {
          const roles = [
            this.applicant.additionalDetails.role,
            ...this.additionalRoles.map((person) => person.role),
          ].reduce((acc, role) => {
            let { grantors, trustees } = acc;
            if (role === 'grantor') grantors += 1;
            if (role === 'trustee') trustees += 1;
            if (role === 'trustee/grantor') {
              trustees += 1;
              grantors += 1;
            }
            return { grantors, trustees };
          }, { grantors: 0, trustees: 0 });

          return roles.grantors >= this.MAX_GRANTORS && roles.trustees >= this.MAX_TRUSTEES;
        }
        return false;
      },
    },
    maxAuthoritiesAdded: {
      get() {
        if (this.applicant && this.applicant.additionalDetails) {
          const roles = [
            {
              hasAmendRevoke: this.applicant.additionalDetails.hasAmendRevoke,
              hasAppointRemove: this.applicant.additionalDetails.hasAppointRemove,
            },
            ...this.additionalRoles.map((person) => (
              {
                hasAmendRevoke: person.hasAmendRevoke,
                hasAppointRemove: person.hasAppointRemove,
              }
            )),
          ].reduce((acc, authorities) => {
            let { amendRevoke, appointRemove } = acc;
            const { hasAmendRevoke, hasAppointRemove } = authorities;
            if (hasAmendRevoke) amendRevoke += 1;
            if (hasAppointRemove) appointRemove += 1;
            return { amendRevoke, appointRemove };
          }, { amendRevoke: 0, appointRemove: 0 });

          return roles.amendRevoke >= this.MAX_AMEND_REVOKE && roles.appointRemove >= this.MAX_APPOINT_REMOVE;
        }
        return false;
      },
    },
  },
  mounted() {
    const instanceData = this.getInstanceData();
    this.$store.commit('currentState/updateField', { path: 'stateName', value: 'trust-dashboard' });
    this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: '' });
    this.$store.commit('currentState/updateField', { path: 'currentUserIndex', value: '' });
    this.$store.commit('currentState/updateField', { path: 'navAction', value: '' });
    const { additionalUsers, removeUserIndex } = instanceData.formData;
    if (removeUserIndex && instanceData.currentState.removeUserIndex && !instanceData.currentState.editMode) {
      this.setListInstanceData('formData', additionalUsers.slice(0, -1), 'additionalUsers');
    }
    this.$store.commit('currentState/updateField', { path: 'editMode', value: false });
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    this.removeModalClassIfExists();
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));

    document.title = this.setTitle(this.pageTitle);

    this.applicant = instanceData.formData.primaryUser;
    this.additionalRoles = instanceData.formData.additionalUsers;
    this.accountDetails = instanceData.formData.accountDetails;
    this.hasAdditionalRoles = this.additionalRoles.filter((person) => person.role !== 'authority').length > 0;
    this.hasAdditionalAuthorities = this.additionalRoles.filter((person) => person.role === 'authority').length > 0;
    this.$store.commit('currentState/updateField', { path: 'removeUserIndex', value: false });
    this.isLoading = false;
  },

  methods: {
    nextButtonClicked() {
      this.isLoading = true;
      this.grantorValid = true;
      const isGrantorValid = this.isGrantorSSNMatch();
      if (
        (this.additionalRoles.some((person) => person.role === 'grantor' || person.role === 'trustee/grantor'))
        || (this.applicant.additionalDetails.role === 'grantor' || this.applicant.additionalDetails.role === 'trustee/grantor')
      ) {
        if (isGrantorValid) {
          this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-review-and-confirm' });
          this.performNavigation(this.pd_content.primary_button.value);
        } else {
          this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-ssn-mismatch' });
          this.performNavigation('grantorMismatch');
        }
      } else {
        this.grantorValid = false;
        this.isLoading = false;
      }
    },
    // if Trust is on a Grantor SSN,
    // then we need to check if we have atleast one additional user as living grantor or Primary User(only if Grantor) with Trust SSN
    isGrantorSSNMatch() {
      if (this.accountDetails.taxIDType !== 'ssn') return true;
      if (this.applicant.additionalDetails.role === 'trustee/grantor' && this.applicant.ssn === this.accountDetails.ssn) return true;
      const status = this.additionalRoles.filter((person) => ((person.role === 'grantor' && person.isDeceased === 'No') || person.role === 'trustee/grantor') && person.ssn === this.accountDetails.ssn);
      return status.length > 0;
    },
    addAnotherRole() {
      this.$store.commit('currentState/updateField', { path: 'currentUserIndex', value: this.getInstanceData().formData.additionalUsers.length });
      this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-additional-role' });
      this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: 'trust-dashboard' });
      this.performNavigation(this.pd_content.primary_button.value);
    },
    addAnotherAuthority() {
      this.$store.commit('currentState/updateField', { path: 'destinationState', value: 'trust-authority' });
      this.performNavigation(this.pd_content.primary_button.value);
    },
    openConfirmationModal(index) {
      this.setActiveIndex(index);
      this.isConfirmationModalOpen = true;
    },
    setActiveIndex(index) {
      this.activeIndex = index;
    },
    closeConfirmationModal() {
      this.isConfirmationModalOpen = false;
      this.setActiveIndex(null);
    },
    removeUser(index) {
      if (this.getInstanceData() && this.getInstanceData().formData && this.getInstanceData().formData.additionalUsers) {
        const { additionalUsers } = this.getInstanceData().formData;
        this.setListInstanceData('formData', [...additionalUsers.slice(0, index), ...additionalUsers.slice(index + 1)], 'additionalUsers');

        this.additionalRoles = this.getInstanceData().formData.additionalUsers;
        this.hasAdditionalRoles = this.additionalRoles.filter((person) => person.role !== 'authority').length > 0;
        this.hasAdditionalAuthorities = this.additionalRoles.filter((person) => person.role === 'authority').length > 0;
      }
      this.closeConfirmationModal();
    },
    editUser(index, page) {
      this.$store.commit('currentState/updateField', { path: 'destinationState', value: page });
      this.$store.commit('currentState/updateField', { path: 'editMode', value: true });
      this.$store.commit('currentState/updateField', { path: 'navAction', value: '' });
      this.$store.commit('currentState/updateField', { path: 'userIndex', value: index });
      this.$store.commit('currentState/updateField', { path: 'currentUserIndex', value: index });
      this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: 'trust-dashboard' });
      this.routeEngine(this.getInstanceData());
    },
    editPrimaryUser() {
      this.$store.commit('currentState/updateField', { path: 'stateName', value: 'trust-dashboard' });
      this.$store.commit('currentState/updateField', { path: 'navAction', value: 'previous' });
      this.$store.commit('currentState/updateField', { path: 'editMode', value: false });
      this.$store.commit('currentState/updateField', { path: 'futureDestinationState', value: 'trust-dashboard' });
      this.routeEngine(this.getInstanceData());
    },
    formatRole(role) {
      const preFormat = role === 'trustee/grantor' ? 'trustee and grantor' : role;
      return preFormat.charAt(0).toUpperCase() + preFormat.slice(1);
    },
  },
};

</script>

<style scoped>
  .table-container {
    margin-top: 1rem;
  }
  .pvd-table-simple {
    width: 100%;
  }
  .display-contents {
     display: contents;
  }
  .add-margin {
    margin-top: 1.5em;
  }
  .add-margin-1 {
    margin-top: 1em;
  }
  .text-left-1pl {
    text-align: left !important;
    padding-left: 1em;
  }
  .pr-1 {
    padding-right: 1rem;
  }
  .actions-column {
    min-width: 130px;
    width: 130px;
  }
  table {
    border-collapse: collapse;
  }
  table th {
    border-top: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 0.75em;
  }

  table th:last-child {
    border-top: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 0.75em;
  }

  table td {
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
  }

  table td:last-child {
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-left: 1px solid #ccc;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
  }
</style>
